import { useEffect } from 'react';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import Icon from '../Unistyles/Icon';
import type { NotifyVariant } from './Notify.types';
import { useNotifyStore } from './Notify.utils';

const ENTER_DURATION = 500;
const EXIT_DURATION = 500;
const DEFAULT_HOLD_DURATION = 1000;

export default function NotifyPopover() {
  const translateY = useSharedValue<number>(0);
  const opacity = useSharedValue<number>(0);
  const { value, hide } = useNotifyStore();
  const variant = value?.backgroundColor ? undefined : value?.variant;

  style.useVariants({
    variant: variant ?? 'success',
  });

  useEffect(() => {
    if (!value) return;

    const holdDuration = value.duration || DEFAULT_HOLD_DURATION;
    const totalDuration = ENTER_DURATION + holdDuration + EXIT_DURATION;

    const fadeIn = withTiming(1, { duration: ENTER_DURATION });
    const holdFadeIn = withTiming(1, {
      duration: holdDuration,
    });
    const fadeOut = withTiming(0, { duration: EXIT_DURATION });

    const translate = withTiming(-30, { duration: ENTER_DURATION });

    const holdTranslate = withTiming(-30, {
      duration: holdDuration,
    });

    const fadeTranslate = withTiming(-60, { duration: EXIT_DURATION });

    opacity.value = withSequence(fadeIn, holdFadeIn, fadeOut);
    translateY.value = withSequence(translate, holdTranslate, fadeTranslate);

    const timeoutId = setTimeout(() => {
      translateY.value = 0;
      opacity.value = 0;
      hide();
    }, totalDuration);

    return () => {
      hide();
      clearTimeout(timeoutId);
    };
  }, [hide, opacity, translateY, value]);

  const animatedStyles = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  if (!value?.icon) return null;

  return (
    <Animated.View style={[style.popover, animatedStyles]}>
      <Animated.View style={style.inner(value.backgroundColor)}>
        <Icon name={value.icon} size="3xl" variant="white" />
        <Text variant="white" size="xl" style={style.text}>
          {value.text}
        </Text>
      </Animated.View>
    </Animated.View>
  );
}

const style = StyleSheet.create((theme) => ({
  popover: {
    alignSelf: 'center',
    position: 'absolute',
    top: '45%',
  },
  inner: (backgroundColor: string | undefined) => ({
    alignItems: 'center',
    backgroundColor,
    borderRadius: theme.borderRadius['3xl'],
    flexDirection: 'row',
    gap: theme.space('lg'),
    justifyContent: 'center',
    padding: theme.space('xl'),
    variants: {
      variant: {
        error: {
          backgroundColor: theme.colors.error,
        },
        info: {
          backgroundColor: theme.colors.info,
        },
        primary: {
          backgroundColor: theme.colors.primary,
        },
        secondary: {
          backgroundColor: theme.colors.secondary,
        },
        success: {
          backgroundColor: theme.colors.success,
        },
        warning: {
          backgroundColor: theme.colors.warning,
        },
      } satisfies Record<NotifyVariant, { backgroundColor: string }>,
    },
  }),
  text: {
    textAlign: 'center',
  },
}));
